'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'

/**
 * Lightweight loading flags for shell chrome (refresh button, etc.).
 *
 * Intentionally does **not** fake multi-step progress on mount or refresh —
 * that produced stacked, professional-looking delays. Callers own real async
 * work and call completeRefresh when done.
 */

interface LoadingState {
  isInitializing: boolean
  isRefreshing: boolean
  isAutoRefreshing: boolean
  isLoading: boolean
  progress: number
  message: string
  lastRefreshTime?: Date
}

interface LoadingContextType {
  loadingState: LoadingState
  startInitialization: () => void
  completeInitialization: () => void
  startRefresh: () => void
  completeRefresh: () => void
  startAutoRefresh: () => void
  completeAutoRefresh: () => void
  setProgress: (progress: number) => void
  setMessage: (message: string) => void
  resetLoading: () => void
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined)

const IDLE: LoadingState = {
  isInitializing: false,
  isRefreshing: false,
  isAutoRefreshing: false,
  isLoading: false,
  progress: 0,
  message: '',
}

export function useLoading() {
  const context = useContext(LoadingContext)
  if (context === undefined) {
    throw new Error('useLoading must be used within LoadingProvider')
  }
  return context
}

interface LoadingProviderProps {
  children: React.ReactNode
}

export function LoadingProvider({ children }: LoadingProviderProps) {
  const [loadingState, setLoadingState] = useState<LoadingState>(IDLE)

  const startInitialization = useCallback(() => {
    setLoadingState({
      ...IDLE,
      isInitializing: true,
      isLoading: true,
      message: 'Loading…',
    })
  }, [])

  const completeInitialization = useCallback(() => {
    setLoadingState(IDLE)
  }, [])

  /** Flag only — no artificial multi-step delay. Pair with completeRefresh(). */
  const startRefresh = useCallback(() => {
    setLoadingState((prev) => ({
      ...prev,
      isRefreshing: true,
      isLoading: true,
      message: 'Refreshing…',
    }))
  }, [])

  const completeRefresh = useCallback(() => {
    setLoadingState((prev) => ({
      ...prev,
      isRefreshing: false,
      isLoading: false,
      message: '',
      lastRefreshTime: new Date(),
      progress: 0,
    }))
  }, [])

  const startAutoRefresh = useCallback(() => {
    setLoadingState((prev) => ({
      ...prev,
      isAutoRefreshing: true,
      // Keep chrome usable during quiet auto-refresh
      isLoading: false,
      message: '',
    }))
  }, [])

  const completeAutoRefresh = useCallback(() => {
    setLoadingState((prev) => ({
      ...prev,
      isAutoRefreshing: false,
      isLoading: false,
      lastRefreshTime: new Date(),
    }))
  }, [])

  const setProgress = useCallback((progress: number) => {
    setLoadingState((prev) => ({ ...prev, progress }))
  }, [])

  const setMessage = useCallback((message: string) => {
    setLoadingState((prev) => ({ ...prev, message }))
  }, [])

  const resetLoading = useCallback(() => {
    setLoadingState(IDLE)
  }, [])

  const value: LoadingContextType = {
    loadingState,
    startInitialization,
    completeInitialization,
    startRefresh,
    completeRefresh,
    startAutoRefresh,
    completeAutoRefresh,
    setProgress,
    setMessage,
    resetLoading,
  }

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
}
